import { API_BASE } from '../../lib/apiOrigin';
import { useAdminLanguage } from "../../i18n/adminTranslations";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  RefreshCw, Plus, Trash2, Edit3, Eye, EyeOff, X, Save,
} from "lucide-react";
import {
  InstagramIcon, FacebookIcon, YoutubeIcon, TiktokIcon, LinkedinIcon,
  YelpIcon, NextdoorIcon, GoogleIcon, GlobeIcon,
} from "../SocialIcons";

const API = API_BASE;

// Must match backend _SOCIAL_PLATFORMS set. Order = default display order in the admin picker.
const PLATFORMS = [
  { value: "instagram", label: "Instagram", Icon: InstagramIcon, placeholder: "https://instagram.com/xavierassembler" },
  { value: "facebook", label: "Facebook", Icon: FacebookIcon, placeholder: "https://facebook.com/xavierassembly" },
  { value: "youtube", label: "YouTube", Icon: YoutubeIcon, placeholder: "https://youtube.com/@xavierassembly" },
  { value: "tiktok", label: "TikTok", Icon: TiktokIcon, placeholder: "https://tiktok.com/@xavierassembler" },
  { value: "yelp", label: "Yelp", Icon: YelpIcon, placeholder: "https://yelp.com/biz/…" },
  { value: "nextdoor", label: "Nextdoor", Icon: NextdoorIcon, placeholder: "https://nextdoor.com/pages/…" },
  { value: "google_business", label: "Google Business", Icon: GoogleIcon, placeholder: "https://g.page/…" },
  { value: "linkedin", label: "LinkedIn", Icon: LinkedinIcon, placeholder: "https://linkedin.com/company/…" },
  { value: "other", label: "Other", Icon: GlobeIcon, placeholder: "https://…" },
];

const iconFor = (slug) => (PLATFORMS.find((p) => p.value === slug) || {}).Icon || GlobeIcon;

const emptyDraft = () => ({
  platform: "instagram",
  label: "",
  url: "",
  display_order: 0,
  is_active: true,
});

const SocialLinksTab = ({ token }) => {
  const { a } = useAdminLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/social-links`, {
        headers: { "X-Admin-Token": token },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load social links.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setDraft({ ...emptyDraft(), display_order: items.length });
    setEditingId("new");
  };
  const startEdit = (item) => {
    setDraft({ ...emptyDraft(), ...item });
    setEditingId(item.id);
  };
  const cancelEdit = () => {
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const save = async () => {
    if (!draft.url.trim() || draft.url.length < 4) {
      setError("URL is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId === "new") {
        const res = await axios.post(`${API}/admin/social-links`, draft, {
          headers: { "X-Admin-Token": token },
        });
        setItems((prev) => [...prev, res.data]);
      } else {
        const res = await axios.put(`${API}/admin/social-links/${editingId}`, draft, {
          headers: { "X-Admin-Token": token },
        });
        setItems((prev) => prev.map((i) => (i.id === editingId ? res.data : i)));
      }
      cancelEdit();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this social link?")) return;
    try {
      await axios.delete(`${API}/admin/social-links/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      setError("Delete failed.");
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await axios.put(
        `${API}/admin/social-links/${item.id}`,
        { is_active: !item.is_active },
        { headers: { "X-Admin-Token": token } }
      );
      setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
    } catch (e) {
      setError("Failed to toggle visibility.");
    }
  };

  const move = async (item, dir) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const other = items[swap];
    const optimistic = [...items];
    optimistic[idx] = { ...other, display_order: item.display_order };
    optimistic[swap] = { ...item, display_order: other.display_order };
    setItems(optimistic);
    try {
      await Promise.all([
        axios.put(`${API}/admin/social-links/${item.id}`, { display_order: other.display_order }, { headers: { "X-Admin-Token": token } }),
        axios.put(`${API}/admin/social-links/${other.id}`, { display_order: item.display_order }, { headers: { "X-Admin-Token": token } }),
      ]);
    } catch (e) {
      setError("Reorder failed. Refreshing.");
      load();
    }
  };

  return (
    <div data-testid="social-links-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl mb-2">{a("Social Media")}</h2>
          <p className="text-zinc-300 text-base max-w-2xl leading-relaxed">
            Links shown in the site footer and contact bar. Reorder with ↑/↓.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="social-new"
            onClick={startNew}
            className="inline-flex items-center gap-2 border border-sky-400 text-sky-300 hover:bg-sky-500/10 px-5 py-3 text-base font-bold uppercase tracking-[0.12em]"
          >
            <Plus size={18} /> {a("New")}
          </button>
          <button
            data-testid="social-refresh"
            onClick={load}
            className="h-11 w-11 border border-white/15 text-white hover:border-sky-400 flex items-center justify-center"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="social-error" className="text-red-300 text-base mb-5 bg-red-500/10 border border-red-500/30 px-4 py-3">
          {a(error)}
        </div>
      )}

      {editingId && (
        <SocialLinkForm
          draft={draft}
          setDraft={setDraft}
          onCancel={cancelEdit}
          onSave={save}
          saving={saving}
          isNew={editingId === "new"}
        />
      )}

      <div className="space-y-3 mt-6">
        {items.length === 0 && !loading ? (
          <div className="border border-white/10 p-10 text-center text-zinc-300">
            No social links yet. Click <span className="text-sky-300 font-semibold">+ New</span>.
          </div>
        ) : (
          items.map((item, idx) => {
            const Icon = iconFor(item.platform);
            return (
              <div
                key={item.id}
                data-testid={`social-card-${item.id}`}
                className={`border p-4 sm:p-5 flex items-center gap-4 ${
                  item.is_active ? "border-white/15 bg-zinc-950/60" : "border-white/5 bg-zinc-950/30 opacity-60"
                }`}
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => move(item, -1)}
                    disabled={idx === 0}
                    className="h-6 w-6 border border-white/10 text-zinc-400 hover:text-sky-400 hover:border-sky-400 disabled:opacity-30 flex items-center justify-center text-sm"
                    title={a("Move up")}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(item, +1)}
                    disabled={idx === items.length - 1}
                    className="h-6 w-6 border border-white/10 text-zinc-400 hover:text-sky-400 hover:border-sky-400 disabled:opacity-30 flex items-center justify-center text-sm"
                    title={a("Move down")}
                  >
                    ↓
                  </button>
                </div>
                <div className="h-11 w-11 border border-white/15 text-sky-300 flex items-center justify-center shrink-0">
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-base capitalize">
                    {item.label || item.platform.replace("_", " ")}
                    {!item.is_active && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">{a("Hidden")}</span>
                    )}
                  </div>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-zinc-400 text-sm break-all hover:text-sky-300"
                  >
                    {item.url}
                  </a>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  <button
                    data-testid={`social-edit-${item.id}`}
                    onClick={() => startEdit(item)}
                    className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-sky-400 hover:text-sky-300 px-3 py-2 text-sm font-semibold"
                  >
                    <Edit3 size={14} /> {a("Edit")}
                  </button>
                  <button
                    data-testid={`social-toggle-${item.id}`}
                    onClick={() => toggleActive(item)}
                    className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-amber-400 hover:text-amber-300 px-3 py-2 text-sm font-semibold"
                  >
                    {item.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    data-testid={`social-delete-${item.id}`}
                    onClick={() => remove(item.id)}
                    className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-red-400 hover:text-red-300 px-3 py-2 text-sm font-semibold"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const SocialLinkForm = ({ draft, setDraft, onCancel, onSave, saving, isNew }) => {
  const { a } = useAdminLanguage();
  const current = PLATFORMS.find((p) => p.value === draft.platform) || PLATFORMS[0];
  return (
    <div data-testid="social-form" className="border border-sky-400/50 bg-zinc-950 p-6 sm:p-8 space-y-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-black text-white text-2xl">
          {a(isNew ? "New social link" : "Edit social link")}
        </h3>
        <button onClick={onCancel} className="text-zinc-400 hover:text-white p-2" title={a("Close")}>
          <X size={20} />
        </button>
      </div>

      <div>
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Platform *</span>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {PLATFORMS.map((p) => (
            <button
              key={p.value}
              type="button"
              data-testid={`social-platform-${p.value}`}
              onClick={() => setDraft((d) => ({ ...d, platform: p.value }))}
              className={`flex flex-col items-center gap-1 border py-3 transition-colors ${
                draft.platform === p.value
                  ? "border-sky-400 bg-sky-500/10 text-sky-300"
                  : "border-white/15 text-zinc-300 hover:border-white/30"
              }`}
            >
              <p.Icon size={20} />
              <span className="text-[10px] uppercase tracking-[0.1em] font-bold">{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">URL *</span>
        <input
          data-testid="social-input-url"
          value={draft.url}
          onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
          className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
          placeholder={current.placeholder}
          maxLength={600}
        />
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Label (optional)</span>
        <input
          data-testid="social-input-label"
          value={draft.label || ""}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
          className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
          placeholder="e.g. @xavierassembler"
          maxLength={80}
        />
      </label>

      <label className="inline-flex items-center gap-3 select-none">
        <input
          type="checkbox"
          data-testid="social-input-active"
          checked={!!draft.is_active}
          onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
          className="h-5 w-5 accent-sky-500"
        />
        <span className="text-zinc-200 text-sm">{a("Show publicly on the site")}</span>
      </label>

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="border border-white/15 text-zinc-200 hover:border-white/30 px-5 py-3 text-sm font-bold uppercase tracking-[0.12em]"
        >
          {a("Cancel")}
        </button>
        <button
          type="button"
          data-testid="social-form-save"
          onClick={onSave}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} />
          {a(saving ? "Saving…" : "Save")}
        </button>
      </div>
    </div>
  );
};

export default SocialLinksTab;
